/* Blog Page Redesign (ClickUp 86d1runhy) — custom elements for the landing page.
   Loaded deferred by sections/custom-blog-landing.liquid. */
(function () {
  'use strict';

  /* <blog-featured>: featured-posts mosaic. Desktop is a static CSS grid;
     on mobile the track overflows into a swipe carousel, and this element
     reveals the prev/next buttons and keeps their state in sync.
     The carousel layout can opt into an infinite loop (data-infinite): the
     original cards are cloned before and after, and whenever a swipe carries
     the track a whole set past either edge we silently offset scrollLeft by one
     set width. The clones are identical, so the jump is invisible and the track
     never dead-ends — with a peek of the neighbouring post on both edges. */
  class BlogFeatured extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.prevBtn = this.querySelector('[data-prev]');
      this.nextBtn = this.querySelector('[data-next]');
      if (!this.track) return;

      this.isInfinite = this.hasAttribute('data-infinite');
      this.onScroll = this.onScroll.bind(this);
      this.update = this.update.bind(this);

      if (this.isInfinite) this.setupInfinite();

      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.scrollByCard(-1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.scrollByCard(1));

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.resizeObserver = new ResizeObserver(this.update);
      this.resizeObserver.observe(this.track);
      this.update();
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      clearTimeout(this.settleTimer);
    }

    /* Clone the full set of cards before and after the originals, then start
       scrolled onto the first original (with a left-edge peek). */
    setupInfinite() {
      const originals = Array.prototype.slice.call(this.track.children);
      if (originals.length < 2) { this.isInfinite = false; return; }

      /* aria-hidden on its own told screen readers the clones were not there
         while the keyboard could still land in them (audit V10) — the old
         querySelector('a') only ever reached the first link of each card, and
         a card carries the wrap link, the title link and the author link.
         inert removes focus and the accessibility tree in one go; the
         tabIndex sweep is the fallback for browsers without it. */
      const supportsInert = 'inert' in HTMLElement.prototype;

      const cloneSet = (nodes) => nodes.map((node) => {
        const clone = node.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        if (supportsInert) {
          clone.inert = true;
        } else {
          clone.querySelectorAll('a[href], button, input, select, textarea, [tabindex]')
            .forEach((el) => { el.tabIndex = -1; });
        }
        return clone;
      });

      const first = originals[0];
      cloneSet(originals).forEach((n) => this.track.insertBefore(n, first));
      const after = cloneSet(originals);
      after.forEach((n) => this.track.appendChild(n));

      this.startCard = first;      // first original
      this.loopCard = after[0];    // its identical clone, exactly one set ahead

      requestAnimationFrame(() => this.jumpTo(this.startCard.offsetLeft - this.peekOffset()));
    }

    /* The left-edge peek width — matches the CSS scroll-padding-left so the first
       full card lands exactly at its snap position (responsive: mobile vs desktop). */
    peekOffset() {
      return parseFloat(getComputedStyle(this.track).scrollPaddingLeft) || 0;
    }

    /* Exact width of one set of original cards (stride between a card and its
       clone one set away) — recomputed live so it survives image/font reflow. */
    setWidth() {
      return this.loopCard.offsetLeft - this.startCard.offsetLeft;
    }

    /* Instant scroll — override the track's CSS scroll-behavior: smooth so the
       seamless loop reset never animates. */
    jumpTo(x) {
      const prev = this.track.style.scrollBehavior;
      this.track.style.scrollBehavior = 'auto';
      this.track.scrollLeft = x;
      this.track.style.scrollBehavior = prev;
    }

    cardStep() {
      const card = this.track.querySelector('.cbl-feature');
      return card ? card.getBoundingClientRect().width + 20 : this.track.clientWidth;
    }

    scrollByCard(direction) {
      this.track.scrollBy({ left: direction * this.cardStep(), behavior: 'smooth' });
    }

    onScroll() {
      this.update();
      /* Reposition only once scrolling has settled — jumping mid-scroll would
         interrupt the smooth arrow animation or a swipe's momentum. */
      if (this.isInfinite) {
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(() => this.recenter(), 150);
      }
    }

    /* Seamless wrap: keep the scroll position within the middle set. When it has
       drifted a whole set past either side, offset by one identical set — the
       clones match the originals, so the jump is invisible. */
    recenter() {
      const set = this.setWidth();
      if (!set) return;
      let x = this.track.scrollLeft;
      const start = x;
      while (x < set * 0.5) x += set;
      while (x >= set * 1.5) x -= set;
      if (Math.abs(x - start) > 1) this.jumpTo(x);
    }

    update() {
      if (this.isInfinite) {
        if (this.prevBtn) this.prevBtn.hidden = false;
        if (this.nextBtn) this.nextBtn.hidden = false;
        return;
      }
      if (!this.prevBtn || !this.nextBtn) return;
      const overflow = this.track.scrollWidth - this.track.clientWidth;
      const scrollable = overflow > 1;
      this.prevBtn.hidden = !scrollable;
      this.nextBtn.hidden = !scrollable;
      if (!scrollable) return;
      this.prevBtn.disabled = this.track.scrollLeft <= 0;
      this.nextBtn.disabled = this.track.scrollLeft >= overflow - 1;
    }
  }

  if (!customElements.get('blog-featured')) customElements.define('blog-featured', BlogFeatured);

  /* Keep the sticky sidebar clear of the sticky header: expose
     --cbl-sticky-top = #StickyHeaderWrap height + 20px, kept in sync as the
     header resizes (announcement bar, viewport changes). The CSS falls back to
     a static value when the header or JS is absent. */
  function initStickySidebarOffset() {
    var header = document.getElementById('StickyHeaderWrap');
    var sidebar = document.querySelector('.cbl-sidebar');
    if (!header || !sidebar) return;
    var GAP = 20;
    function update() {
      var height = header.offsetHeight;
      if (height > 0) {
        document.documentElement.style.setProperty('--cbl-sticky-top', (height + GAP) + 'px');
      }
    }
    update();
    if ('ResizeObserver' in window) {
      new ResizeObserver(update).observe(header);
    }
    window.addEventListener('resize', update, { passive: true });
  }

  initStickySidebarOffset();
})();
